import { IonList, IonText } from "@ionic/react";
import { ChangeEvent, KeyboardEvent, MouseEvent, ReactNode, useCallback } from "react";
import { Control, Controller, FieldError, FieldPath, FieldValues } from "react-hook-form";

import Error from "@components/fields/Error";
import styles from "@components/fields/IconRadioButtonGroup.module.css";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import Icon from "@components/text/Icon";
import InputLabel from "@components/typography/InputLabel";
import { IconName } from "@utils/iconUtils";

type RadioOption = {
  help?: string;
  iconName: IconName | number;
  label: string;
  value: string | null;
};

type Props<FormData extends FieldValues> = {
  allowDeselection?: boolean;
  control: Control<FormData>;
  error: FieldError | undefined;
  inTracker?: boolean;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  noSpacing?: boolean;
  options: RadioOption[];
  required?: boolean;
};

const IconRadioButtonGroup = <FormData extends FieldValues>({
  allowDeselection = false,
  control,
  error,
  inTracker,
  label,
  name,
  noSpacing = false,
  options,
  required = false
}: Props<FormData>) => {
  const generateKeyDownHandler = useCallback(
    (onChange: (value: string | null) => void, value: string | null, optionValue: string | null) =>
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
          if (allowDeselection && value === optionValue) {
            onChange(null);
          } else {
            onChange(optionValue);
          }
          event.stopPropagation();
          event.preventDefault();
        }
      },
    [allowDeselection]
  );

  const handleHelpClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return (
    <div className={`${styles.radioGroup} ${styles.normalRadioGroup}`}>
      <div className={noSpacing ? "" : styles.labelSpacing}>
        {label && (
          <div className={inTracker ? styles.centeredLabel : ""} id={`${name}-label`}>
            <InputLabel>
              {label}
              {required && <RequiredFieldIndicator />}
            </InputLabel>
          </div>
        )}
      </div>
      <div className={styles.options}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <IonList
              aria-describedby={error ? `${name}-error` : ""}
              aria-invalid={error?.message?.length ? "true" : "false"}
              aria-labelledby={label ? `${name}-label` : ""}
              aria-required={required}
              className={`${styles.list} ${inTracker ? styles.trackerList : ""}`}
            >
              {options.map(option => (
                <div
                  className={option.value === value ? styles.checked : styles.notChecked}
                  data-test="radio-option"
                  key={`${option.label}-${option.value ?? "null"}`}
                  onKeyDown={generateKeyDownHandler(onChange, value as string | null, option.value)}
                  tabIndex={0}
                >
                  <label className={styles.groupLabel}>
                    {typeof option.iconName === "string" && (
                      <div className={styles.icon}>
                        <Icon data-test="icon" icon={option.iconName} size="large" />
                      </div>
                    )}
                    {typeof option.iconName === "number" && (
                      <div className={styles.icon}>
                        <div className={styles.hugeIconText}>
                          <IonText>{option.iconName}</IonText>
                        </div>
                      </div>
                    )}
                    <input
                      aria-label={option.label ?? undefined}
                      checked={option.value === value}
                      className={styles.input}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (allowDeselection && !event.target.checked) {
                          onChange(null);
                        } else if (event.target.value === "") {
                          const emptyOption = options.find(option => !option.value);
                          onChange(emptyOption?.value);
                        } else {
                          onChange(event.target.value);
                        }
                      }}
                      type="checkbox"
                      value={option.value !== null ? option.value : undefined}
                    />
                    <div className={styles.optionLabel}>
                      <IonText>{option.label}</IonText>
                    </div>
                    {option.help && (
                      <div className={styles.help} onClick={handleHelpClick}>
                        <div className={styles.tooltipTop}>
                          <div className={styles.tooltipMessage}>{option.help}</div>
                        </div>
                        <Icon icon="questionCircle" />
                      </div>
                    )}
                  </label>
                </div>
              ))}
            </IonList>
          )}
        />
      </div>
      {error && (
        <div id={`${name}-error`}>
          <Error error={error} />
        </div>
      )}
    </div>
  );
};

export default IconRadioButtonGroup;
